import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Trophy, User, Menu, X, Home, Crown, Award, History, Play, Shield, LogOut, Bell, Lightbulb } from 'lucide-react';
import SubscribeModal from '../SubscribeModal';
import axios from 'axios';
import { handleTwitchLogin } from '../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, isAdmin, token, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [adminNotifCount, setAdminNotifCount] = useState(0);
  const [userNotifCount, setUserNotifCount] = useState(0);

  // Fetch admin notification count
  useEffect(() => {
    const fetchAdminNotifCount = async () => {
      if (!isAdmin || !token) return;
      try {
        const res = await axios.get(`${API}/admin/notifications/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAdminNotifCount(res.data.count || 0);
      } catch (e) {
        // silent fail
      }
    };
    
    fetchAdminNotifCount();
    // Poll every 30 seconds
    const interval = setInterval(fetchAdminNotifCount, 30000);
    return () => clearInterval(interval);
  }, [isAdmin, token]);

  // Fetch user notification count
  useEffect(() => {
    const fetchUserNotifCount = async () => {
      if (!isAuthenticated || !token) return;
      try {
        const res = await axios.get(`${API}/notifications/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserNotifCount(res.data?.unread_count || 0);
      } catch (error) {
        console.error('Failed to fetch user notification count:', error);
      }
    };
    
    fetchUserNotifCount();
    // Poll every 30 seconds
    const interval = setInterval(fetchUserNotifCount, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, token]);

  const navItems = [
    { path: '/', label: 'Home', icon: Home, iconClass: 'text-blue-400' },
    { path: '/winners', label: 'Winners', icon: Crown, iconClass: 'text-yellow-400' },
    { path: '/leaderboard', label: 'Leaderboard', icon: Award, iconClass: 'text-orange-400' },
    { path: '/history', label: 'History', icon: History, iconClass: 'text-purple-400' },
    { path: '/gallery', label: 'Gallery', icon: Play, iconClass: 'text-red-500' },
    { path: '/feedback', label: 'Ideas', icon: Lightbulb, iconClass: 'text-green-400' },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogin = handleTwitchLogin;

  return (
    <nav className="fixed top-0 w-full z-50 border-b border-red-900/30 bg-black/90 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 h-14 sm:h-16 flex items-center justify-between">
        {/* Logo */}
        <div 
          className="flex items-center gap-2 cursor-pointer flex-shrink-0" 
          onClick={() => navigate('/')}
          data-testid="header-logo"
        >
          <img 
            src="/logo.png" 
            alt="CesarM Challenges" 
            className="w-12 h-12 sm:w-14 sm:h-14 object-contain"
          />
        </div>

        {/* Desktop/Tablet Navigation */}
        <div className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <Button 
              key={item.path}
              variant="ghost" 
              size="sm" 
              className={`text-sm ${
                isActive(item.path) 
                  ? 'text-red-400 bg-red-500/10' 
                  : 'text-gray-300 hover:text-white hover:bg-white/10'
              }`}
              onClick={() => navigate(item.path)}
              data-testid={`nav-${item.label.toLowerCase()}-btn`}
            >
              <item.icon className={`w-4 h-4 mr-1 ${item.iconClass || ''}`} />
              {item.label}
            </Button>
          ))}
          
          {isAdmin && (
            <Button 
              variant="ghost" 
              size="sm" 
              className={`text-sm relative ${
                isActive('/admin') 
                  ? 'text-yellow-400 bg-yellow-500/10' 
                  : 'text-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/10'
              }`}
              onClick={() => navigate('/admin')}
              data-testid="nav-admin-btn"
            >
              <Shield className="w-4 h-4 mr-1" />
              Admin
              {adminNotifCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-red-500/50">
                  {adminNotifCount > 99 ? '99+' : adminNotifCount}
                </span>
              )}
            </Button>
          )}
          
          {/* Subscribe Button */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-sm text-green-400 hover:text-green-300 hover:bg-green-500/10"
            onClick={() => setShowSubscribe(true)}
            data-testid="header-subscribe-btn"
          >
            <Bell className="w-4 h-4 mr-1" />
            Subscribe
          </Button>
        </div>

        {/* User Actions - Desktop */}
        <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
          {isAuthenticated ? (
            <>
              <Button 
                variant="ghost" 
                size="sm" 
                className={`text-sm relative ${
                  isActive('/dashboard') 
                    ? 'text-red-400 bg-red-500/10' 
                    : 'text-gray-300 hover:text-white'
                }`}
                onClick={() => navigate('/dashboard')}
                data-testid="nav-dashboard-btn"
              >
                <User className="w-4 h-4 mr-1" />
                {user?.display_name || 'Dashboard'}
                {userNotifCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-red-500/50">
                    {userNotifCount > 99 ? '99+' : userNotifCount}
                  </span>
                )}
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-gray-400 hover:text-red-400"
                onClick={logout}
                data-testid="nav-logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <Button 
              size="sm" 
              className="bg-purple-600 hover:bg-purple-700 text-sm"
              onClick={handleLogin}
              data-testid="nav-login-btn"
            >
              <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
              </svg>
              Login with Twitch
            </Button>
          )}
        </div>

        {/* Tablet Navigation - Compact with labels */}
        <div className="hidden md:flex lg:hidden items-center gap-0.5">
          {navItems.map((item) => (
            <Button 
              key={item.path}
              variant="ghost" 
              size="sm" 
              className={`text-xs px-2 ${
                isActive(item.path) 
                  ? 'text-red-400 bg-red-500/10' 
                  : 'text-gray-300 hover:text-white hover:bg-white/10'
              }`}
              onClick={() => navigate(item.path)}
            >
              <item.icon className={`w-3.5 h-3.5 mr-1 ${item.iconClass || ''}`} />
              {item.label}
            </Button>
          ))}
          
          {isAdmin && (
            <Button 
              variant="ghost" 
              size="sm" 
              className={`text-xs px-2 relative ${
                isActive('/admin') 
                  ? 'text-yellow-400 bg-yellow-500/10' 
                  : 'text-yellow-500 hover:text-yellow-400'
              }`}
              onClick={() => navigate('/admin')}
            >
              <Shield className="w-3.5 h-3.5 mr-1" />
              Admin
              {adminNotifCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-red-500/50">
                  {adminNotifCount > 99 ? '99+' : adminNotifCount}
                </span>
              )}
            </Button>
          )}
          
          {/* Subscribe Button - Tablet */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-xs px-2 text-green-400 hover:text-green-300 hover:bg-green-500/10"
            onClick={() => setShowSubscribe(true)}
            data-testid="header-subscribe-btn-tablet"
          >
            <Bell className="w-3.5 h-3.5 mr-1" />
            Subscribe
          </Button>
          
          {isAuthenticated ? (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs px-2 text-gray-300 relative"
              onClick={() => navigate('/dashboard')}
            >
              <User className="w-3.5 h-3.5 mr-1" />
              {user?.display_name?.slice(0, 8) || 'Dashboard'}
              {userNotifCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-red-500/50">
                  {userNotifCount > 99 ? '99+' : userNotifCount}
                </span>
              )}
            </Button>
          ) : (
            <Button 
              size="sm" 
              className="bg-purple-600 hover:bg-purple-700 text-xs px-2 ml-1"
              onClick={handleLogin}
              data-testid="nav-login-btn-tablet"
            >
              <svg className="w-3.5 h-3.5 mr-1" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
              </svg>
              Login
            </Button>
          )}
        </div>

        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="sm"
          className="md:hidden text-white"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-black/95 border-b border-red-900/30 px-4 py-3 space-y-2">
          {navItems.map((item) => (
            <Button 
              key={item.path}
              variant="ghost" 
              className={`w-full justify-start text-sm ${
                isActive(item.path) 
                  ? 'text-red-400 bg-red-500/10' 
                  : 'text-gray-300 hover:text-white'
              }`}
              onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
            >
              <item.icon className={`w-4 h-4 mr-2 ${item.iconClass || ''}`} />
              {item.label}
            </Button>
          ))}
          
          {isAdmin && (
            <Button 
              variant="ghost" 
              className={`w-full justify-start text-sm ${
                isActive('/admin') 
                  ? 'text-yellow-400 bg-yellow-500/10' 
                  : 'text-yellow-500'
              }`}
              onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
            >
              <Shield className="w-4 h-4 mr-2" />
              Admin
              {adminNotifCount > 0 && (
                <span className="ml-2 min-w-[20px] h-[20px] px-1.5 bg-red-500 text-white text-xs font-bold rounded-full inline-flex items-center justify-center animate-pulse">
                  {adminNotifCount > 99 ? '99+' : adminNotifCount}
                </span>
              )}
            </Button>
          )}
          
          {/* Subscribe Button - Mobile */}
          <Button 
            variant="ghost" 
            className="w-full justify-start text-sm text-green-400 hover:text-green-300 hover:bg-green-500/10"
            onClick={() => { setShowSubscribe(true); setMobileMenuOpen(false); }}
            data-testid="header-subscribe-btn-mobile"
          >
            <Bell className="w-4 h-4 mr-2" />
            Subscribe to Notifications
          </Button>
          
          <div className="pt-2 border-t border-red-900/30">
            {isAuthenticated ? (
              <>
                <Button 
                  variant="ghost" 
                  className="w-full justify-start text-sm text-gray-300"
                  onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                >
                  <User className="w-4 h-4 mr-2" />
                  {user?.display_name || 'Dashboard'}
                  {userNotifCount > 0 && (
                    <span className="ml-2 min-w-[20px] h-[20px] px-1.5 bg-red-500 text-white text-xs font-bold rounded-full inline-flex items-center justify-center animate-pulse">
                      {userNotifCount > 99 ? '99+' : userNotifCount}
                    </span>
                  )}
                </Button>
                <Button 
                  variant="ghost" 
                  className="w-full justify-start text-sm text-gray-400"
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </>
            ) : (
              <Button 
                className="w-full bg-purple-600 hover:bg-purple-700 text-sm"
                onClick={() => { handleLogin(); setMobileMenuOpen(false); }}
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                </svg>
                Login with Twitch
              </Button>
            )}
          </div>
        </div>
      )}
      
      {/* Subscribe Modal */}
      <SubscribeModal isOpen={showSubscribe} onClose={() => setShowSubscribe(false)} />
    </nav>
  );
};

export default Header;
